'use client';

import { useTranslations } from '@power-core/toolkit';
import type { FunctionComponent } from 'react';
import { AbbClose } from '@/assets/icons/components/abb-close';
import { AbbSearch } from '@/assets/icons/components/abb-search';
import { useAppContext } from '@/modules/app-context/use-app-context';
import { ProjectList } from '@/modules/project/components/project-list/project-list';
import { useProjectListParams } from '@/modules/project/hooks/use-project-list-params';
import { useDebounce } from '@/modules/utils/custom-hooks/use-debounce';
import { useDevice } from '@/modules/utils/custom-hooks/use-device';
import { useNavigation } from '@/modules/utils/custom-hooks/use-navigation';
import { useTheme } from '@/modules/utils/custom-hooks/use-theme';
import { Button } from '@/ui-components/button/button';
import { Card } from '@/ui-components/card/card';
import { SearchableDropdown } from '@/ui-components/dropdown/searchable-dropdown';
import { Flexbox } from '@/ui-components/flexbox/flexbox';
import { Input } from '@/ui-components/input/input';
import { Paragraph } from '@/ui-components/paragraph/paragraph';
import { AuthButtonGroup } from '../auth-buttons-group/auth-button-group';
import { useAuth } from '@power-core/toolkit';

export const Sidebar: FunctionComponent = () => {
	const t = useTranslations();
	const { auth } = useAuth();
	const { searchProjectValue, setSearchProjectValue } = useAppContext();
	const { isLaptop } = useDevice();
	const { isDarkTheme } = useTheme();
	const { addSearchParam } = useNavigation();
	const debouncedValue = useDebounce(searchProjectValue);
	const { projects, isPending, openIndex, handleToggle, setSort } = useProjectListParams({
		title: debouncedValue,
	});

	const sortOptions = [
		{
			label: t('T_BY_NAME_ASC'),
			value: 'title:asc',
		},
		{
			label: t('T_BY_NAME_DESC'),
			value: 'title:desc',
		},
		{
			label: t('T_BY_LAST_CHANGE_ASC'),
			value: 'lastUpdatedDate:asc',
		},
		{
			label: t('T_BY_LAST_CHANGE_DESC'),
			value: 'lastUpdatedDate:desc',
		},
	];

	return (
		<div className='step-1 break-words' style={{ height: `calc(100vh - ${isLaptop ? 132 : 152}px)` }}>
			<Card fullHeight className='dark:bg-card_dark'>
				{auth.status === 'authenticated' ? (
					<>
						<Flexbox className='gap-2 flex-wrap' justify='end'>
							<Button
								onClick={() => addSearchParam({ key: 'modal', value: 'ProjectModal' })}
								variant='ghost-secondary'
								sizing='small'
								className='w-auto text-dark'
								disabled={isPending}
							>
								{t('T_ADD_PROJECT')}
							</Button>
						</Flexbox>

						<Input
							className='mt-2 w-full'
							sizing='small'
							placeholder={t('T_SEARCH_PROJECT')}
							disabled={isPending}
							value={searchProjectValue}
							onChange={(e) => setSearchProjectValue(e.target.value)}
							icon={
								searchProjectValue ? (
									<AbbClose
										size={20}
										className='cursor-pointer'
										fill={isDarkTheme ? '#f1f1f1' : '#1f1f1f'}
										onClick={() => setSearchProjectValue('')}
									/>
								) : (
									<AbbSearch size={14} className={isDarkTheme ? '' : 'opacity-30'} fill={isDarkTheme ? '#f1f1f1' : '#1f1f1f'} />
								)
							}
						/>

						<SearchableDropdown
							className='mt-2'
							options={sortOptions}
							placeholder={t('T_SORT_PROJECTS')}
							onSelect={(value) => !Array.isArray(value) && setSort(value)}
							onClear={() => setSort(undefined)}
							isSearchable={false}
							inputBackground='light'
							sizing='small'
							hasDatePicker
						/>

						<div className='h-full rounded-lg overflow-auto scrollbar-rounded mt-2'>
							<ProjectList projects={projects} openIndex={openIndex} handleToggleAction={handleToggle} isPending={isPending} />
						</div>
					</>
				) : (
					<Flexbox direction='col' justify='center' className='gap-3 mt-5'>
						<Paragraph className='text-center'>{t('T_SIGN_IN_TO_START')}</Paragraph>
						<AuthButtonGroup />
					</Flexbox>
				)}
			</Card>
		</div>
	);
};
